
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layout } from "@/components/Layout";
import { Calendar, Archive, CreditCard } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserSubscriptions } from "@/components/UserSubscriptions";
import { useAuth } from "@/lib/hooks/auth";
import { LoadingDashboard } from "@/components/dashboard/LoadingDashboard";
import { DashboardHeader } from "@/components/dashboard/DashboardHeader";
import { CurrentBookings } from "@/components/dashboard/CurrentBookings";
import { BookingHistory } from "@/components/dashboard/BookingHistory";
import { CancelBookingDialog } from "@/components/dashboard/CancelBookingDialog";
import { useBookings } from "@/hooks/dashboard/useBookings";
import { useSubscriptions } from "@/hooks/dashboard/useSubscriptions";
import { toast } from "sonner";

const UserDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading: authLoading, signOut } = useAuth();
  const [activeTab, setActiveTab] = useState("bookings");
  
  // Redirect to login if user is not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      toast.error(t("dashboard.login.required"));
      navigate("/login");
    }
  }, [user, authLoading, navigate, t]);
  
  // Use custom hooks to manage bookings and subscriptions
  const {
    currentBookings,
    pastBookings,
    isLoading,
    bookingToCancel,
    isCancelling,
    isBookingPast,
    canCancelBooking,
    handleCancelBooking,
    handleConfirmCancel,
    handleCancelDialogClose
  } = useBookings(user?.id);
  
  const {
    subscriptions,
    isLoadingSubscriptions,
    handleCreateSubscription,
    fetchSubscriptions
  } = useSubscriptions(user?.id);
  
  const handleLogout = () => {
    signOut();
  };
  
  // Show loading state while auth is being determined
  if (authLoading) {
    return <LoadingDashboard />;
  }
  
  // If no user after loading, redirect happens via useEffect above
  if (!user) {
    return <LoadingDashboard />;
  }
  
  return (
    <Layout>
      <section className="py-12 md:py-20 bg-gradient-to-b from-clay-50 to-background">
        <div className="container mx-auto px-6">
          <DashboardHeader onLogout={handleLogout} />
          
          <Tabs defaultValue="bookings" className="space-y-8" onValueChange={setActiveTab} value={activeTab}>
            <TabsList>
              <TabsTrigger value="bookings">
                <Calendar className="h-4 w-4 mr-2" />
                {t("dashboard.bookings.title")}
              </TabsTrigger>
              <TabsTrigger value="history">
                <Archive className="h-4 w-4 mr-2" />
                {t("dashboard.bookings.history")}
              </TabsTrigger>
              <TabsTrigger value="subscriptions">
                <CreditCard className="h-4 w-4 mr-2" />
                {t("dashboard.subscriptions.title")}
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="bookings" className="space-y-6">
              <CurrentBookings 
                bookings={currentBookings}
                isLoading={isLoading}
                onCancelBooking={handleCancelBooking}
                isBookingPast={isBookingPast}
                canCancelBooking={canCancelBooking}
              />
            </TabsContent>
            
            <TabsContent value="history" className="space-y-6">
              <BookingHistory
                bookings={pastBookings}
                isLoading={isLoading}
                isBookingPast={isBookingPast}
              />
            </TabsContent>
            
            <TabsContent value="subscriptions">
              {isLoadingSubscriptions ? (
                <div className="space-y-4">
                  {[1, 2].map((i) => (
                    <div key={i} className="h-48 bg-card animate-pulse rounded-xl border border-border"></div>
                  ))}
                </div>
              ) : (
                <UserSubscriptions 
                  userId={user.id || ''}
                  subscriptions={subscriptions}
                  onCreateSubscription={handleCreateSubscription}
                  onRefreshSubscriptions={() => user && fetchSubscriptions(user.id)}
                />
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>
      
      <CancelBookingDialog
        isOpen={bookingToCancel !== null}
        onClose={handleCancelDialogClose}
        onConfirm={handleConfirmCancel}
        isCancelling={isCancelling}
      />
    </Layout>
  );
};

export default UserDashboard;
